import type {HotelDetails} from '@/api/hoteldetails';
import type {SortParams} from '@/modules/common/context/QueryParams/models';
import type {
  OfferEcommerceItem,
  EventAction,
  EventActionCreator,
  EventHandler,
} from '@/redux/middleware/googleTagManager/types';
import type {BestOffer} from '@/redux/v1/offers/list/models';

import {EVENT_BEST_OFFER_BOXES_LOADED} from '@/redux/middleware/googleTagManager/actionTypes';
import {mapBestOfferType} from '@/redux/middleware/googleTagManager/utils/mapBestOfferType';
import {mapToSortListNameForOffer} from '@/redux/middleware/googleTagManager/utils/mapToSortListNameForOffer';
import {mapOfferEcommerceItem} from '@/redux/middleware/googleTagManager/utils/offerData';
import {waitForGtmDomEvent} from '@/redux/middleware/googleTagManager/utils/waitForGtmDomEvent';
import {selectAvailability} from '@/redux/v1/availability/reducer';

type Payload = {
  hotelId?: string;
  bestOfferBoxes: Array<string>;
  hotelDetails: HotelDetails;
  bestOffers: Array<BestOffer>;
  travellerCount: number;
  sortParams: SortParams;
};

type Action = EventAction<Payload>;

type NullEvent = {
  ecommerce: null;
};

type Event = {
  event: string;
  event_action: string;
  non_interaction: true;
  ecommerce: {
    promotion_name: string;
    items: Array<OfferEcommerceItem>;
  };
};

export const action: EventActionCreator<Action, Payload> = (payload) => ({
  type: EVENT_BEST_OFFER_BOXES_LOADED,
  payload,
});

export const handler: EventHandler<
  Action,
  Promise<[NullEvent, Event]>
> = waitForGtmDomEvent<[NullEvent, Event], Payload>((eventAction, state) => {
  const {hotelId, hotelDetails, bestOffers, travellerCount, sortParams} =
    eventAction.payload;

  return [
    {ecommerce: null},
    {
      event: 'view_promotion',
      event_action: 'best_offer_loaded',
      non_interaction: true,
      ecommerce: {
        promotion_name: 'Best offer box',
        items: bestOffers?.map((bestOffer, index) =>
          mapOfferEcommerceItem({
            hotelId,
            travellerCount,
            hotelData: hotelDetails,
            offer: selectAvailability(bestOffer.offerID, state),
            index,
            itemListName: mapToSortListNameForOffer(sortParams.sortBy ?? ''),
            itemBestOfferType: mapBestOfferType(bestOffer.type),
          })
        ),
      },
    },
  ];
});
